import React, { useState, useRef, useEffect, useCallback } from "react";
import HTMLFlipBook from "react-pageflip";
import { Document, Page as PdfPage, pdfjs } from "react-pdf";
import { ChevronLeft, ChevronRight, BookOpen, Maximize2, X } from "lucide-react";
import { Link } from "react-router-dom";
import { FLIPBOOK, MOBILE_BREAKPOINT, ICON_SIZE } from "../constants/ui";
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// PDF URLs in order
const CATALOG_PAGES = [
  {
    id: "couverture",
    title: "Couverture",
    url: "https://customer-assets.emergentagent.com/job_0e82ffdb-5908-4925-9b50-c0440d2f8185/artifacts/oqen6fd6_couverture%202.pdf"
  },
  {
    id: "monture",
    title: "Montures",
    url: "https://customer-assets.emergentagent.com/job_0e82ffdb-5908-4925-9b50-c0440d2f8185/artifacts/ikylbpb3_monture.pdf"
  },
  {
    id: "forme",
    title: "Formes",
    url: "https://customer-assets.emergentagent.com/job_0e82ffdb-5908-4925-9b50-c0440d2f8185/artifacts/cn4qcfr6_forme.pdf"
  },
  {
    id: "verres",
    title: "Verres",
    url: "https://customer-assets.emergentagent.com/job_0e82ffdb-5908-4925-9b50-c0440d2f8185/artifacts/33wmcnsv_verres.pdf"
  },
  {
    id: "dc",
    title: "Diamond Cut",
    url: "https://customer-assets.emergentagent.com/job_0e82ffdb-5908-4925-9b50-c0440d2f8185/artifacts/qfaqaefz_DC%202.pdf"
  },
  {
    id: "tarifs",
    title: "Tarifs",
    url: "https://customer-assets.emergentagent.com/job_0e82ffdb-5908-4925-9b50-c0440d2f8185/artifacts/yhkqmrv8_tarifs%202.pdf"
  }
];

// Calculate fullscreen dimensions
const calculateFullscreenDimensions = () => {
  const availableHeight = window.innerHeight - FLIPBOOK.FULLSCREEN_HEADER_HEIGHT;
  const availableWidth = window.innerWidth - FLIPBOOK.FULLSCREEN_PADDING;
  const heightBasedWidth = availableHeight / FLIPBOOK.A4_RATIO;
  const width = Math.min(heightBasedWidth, availableWidth, FLIPBOOK.FULLSCREEN_MAX_WIDTH);
  const height = width * FLIPBOOK.A4_RATIO;
  return { width: Math.floor(width), height: Math.floor(height) };
};

// Calculate flipbook dimensions
const calculateFlipbookDimensions = () => {
  const mobile = window.innerWidth < MOBILE_BREAKPOINT;
  const maxHeight = window.innerHeight - FLIPBOOK.PAGE_TOP_OFFSET;
  const height = Math.min(maxHeight, mobile ? FLIPBOOK.MOBILE_HEIGHT : FLIPBOOK.DESKTOP_HEIGHT);
  const width = Math.floor(height / FLIPBOOK.A4_RATIO);
  return { width, height, isMobile: mobile };
};

// PDF Page component for flipbook
const PdfPageContent = ({ url, width, height }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const handleLoadSuccess = useCallback(() => {
    setLoading(false);
  }, []);

  const handleLoadError = useCallback(() => {
    setLoading(false);
    setError(true);
  }, []);

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-[#f5f5f5]">
        <p className="text-[#666660] text-sm">Aperçu non disponible</p>
      </div>
    );
  }

  return (
    <div 
      className="relative bg-white overflow-hidden"
      style={{ width, height }}
    >
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
          <div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      <Document
        file={url}
        onLoadSuccess={handleLoadSuccess}
        onLoadError={handleLoadError}
        loading={null}
      >
        <PdfPage 
          pageNumber={1} 
          width={width}
          renderTextLayer={false}
          renderAnnotationLayer={false}
        />
      </Document>
    </div>
  );
};

// Last page CTA component
const LastPageContent = ({ onClose, pageNumber, totalPages }) => (
  <div className="w-full h-full bg-gradient-to-br from-[#1A1A1A] to-[#2D2D2D] flex flex-col items-center justify-center p-6">
    <div className="text-center">
      <BookOpen size={ICON_SIZE.XXXL} className="text-[#D4AF37] mx-auto mb-4" />
      <h2 className="font-serif text-xl md:text-2xl text-white mb-3">
        Créez vos lunettes uniques
      </h2>
      <p className="text-white/70 font-light mb-6 max-w-xs mx-auto text-sm">
        Prenez rendez-vous pour une consultation personnalisée.
      </p>
      <Link
        to="/contact"
        onClick={onClose}
        data-testid="catalog-rdv-button"
        className="inline-block bg-[#D4AF37] text-[#1A1A1A] px-5 py-2.5 text-xs uppercase tracking-[0.1em] font-medium hover:bg-[#C4A030] transition-colors duration-300"
      >
        Prendre RDV
      </Link>
    </div>
    <div className="absolute bottom-3 text-white/40 text-xs">
      Page {pageNumber} / {totalPages}
    </div>
  </div>
);

// Fullscreen PDF viewer component
const FullscreenViewer = ({ pageIndex, onClose, onPrev, onNext, totalPages }) => {
  const [loading, setLoading] = useState(true);
  const [dimensions, setDimensions] = useState(calculateFullscreenDimensions);
  
  const isLastPage = pageIndex >= CATALOG_PAGES.length;
  const pageData = isLastPage ? null : CATALOG_PAGES[pageIndex];
  
  useEffect(() => {
    const updateDimensions = () => {
      setDimensions(calculateFullscreenDimensions());
    };
    
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onPrev, onNext]);

  const handleDocumentLoad = useCallback(() => {
    setLoading(false);
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-center">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-6 py-4">
        <button
          onClick={onPrev}
          disabled={pageIndex === 0}
          className={`p-3 rounded-full transition-all duration-300 ${
            pageIndex === 0
              ? 'bg-white/10 text-white/30 cursor-not-allowed'
              : 'bg-[#D4AF37] text-[#1A1A1A] hover:bg-[#C4A030]'
          }`}
        >
          <ChevronLeft size={ICON_SIZE.XL} />
        </button>
        
        <span className="text-white text-sm">
          {isLastPage ? "Prendre rendez-vous" : pageData.title} ({pageIndex + 1}/{totalPages})
        </span>
        
        <button
          onClick={onNext}
          disabled={pageIndex >= totalPages - 1}
          className={`p-3 rounded-full transition-all duration-300 ${
            pageIndex >= totalPages - 1
              ? 'bg-white/10 text-white/30 cursor-not-allowed'
              : 'bg-[#D4AF37] text-[#1A1A1A] hover:bg-[#C4A030]'
          }`}
        >
          <ChevronRight size={ICON_SIZE.XL} />
        </button>
      </div>

      {/* Content */}
      <div 
        className="bg-white rounded-lg overflow-hidden shadow-2xl"
        style={{ width: dimensions.width, height: dimensions.height }}
      >
        {isLastPage ? (
          <LastPageContent onClose={onClose} pageNumber={totalPages} totalPages={totalPages} />
        ) : (
          <div className="w-full h-full relative">
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
                <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            <Document
              file={pageData.url}
              onLoadSuccess={handleDocumentLoad}
              onLoadError={handleDocumentLoad}
              loading={null}
            >
              <PdfPage 
                pageNumber={1} 
                width={dimensions.width}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Document>
          </div>
        )}
      </div>

      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-full transition-colors"
      >
        <X size={ICON_SIZE.MEDIUM} />
        Fermer
      </button>
    </div>
  );
};

// Page component for the flipbook
const PageComponent = React.forwardRef(({ pageData, pageNumber, totalPages, isLastPage, dimensions }, ref) => {
  if (isLastPage) {
    return (
      <div ref={ref} className="page-content h-full">
        <LastPageContent pageNumber={pageNumber} totalPages={totalPages} />
      </div>
    );
  }

  return (
    <div ref={ref} className="page-content bg-white h-full relative overflow-hidden">
      <PdfPageContent 
        url={pageData.url} 
        width={dimensions.width}
        height={dimensions.height}
      />
    </div>
  );
});

PageComponent.displayName = 'PageComponent';

// Page dots navigation
const PageDots = ({ totalPages, currentPage, onPageSelect }) => (
  <div className="mt-4 flex items-center gap-1.5">
    {[...Array(totalPages)].map((_, index) => (
      <button
        key={`page-dot-${index}`}
        onClick={() => onPageSelect(index)}
        className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
          currentPage === index 
            ? 'bg-[#D4AF37] w-4' 
            : 'bg-white/30 hover:bg-white/50'
        }`}
        aria-label={`Aller à la page ${index + 1}`}
      />
    ))}
  </div>
);

// Navigation arrow button
const NavArrow = ({ direction, disabled, onClick }) => {
  const isLeft = direction === 'left';
  const Icon = isLeft ? ChevronLeft : ChevronRight;
  const positionClass = isLeft ? 'left-0 md:-left-14' : 'right-0 md:-right-14';
  
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`absolute ${positionClass} z-10 p-2 rounded-full transition-all duration-300 ${
        disabled
          ? 'bg-white/10 text-white/30 cursor-not-allowed'
          : 'bg-[#D4AF37] text-[#1A1A1A] hover:bg-[#C4A030]'
      }`}
      aria-label={isLeft ? "Page précédente" : "Page suivante"}
    >
      <Icon size={ICON_SIZE.MEDIUM} />
    </button>
  );
};

export default function CataloguePage() {
  const flipBookRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [dimensions, setDimensions] = useState({ width: FLIPBOOK.DEFAULT_WIDTH, height: FLIPBOOK.DEFAULT_HEIGHT });
  const [isMobile, setIsMobile] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const totalPages = CATALOG_PAGES.length + 1;

  useEffect(() => {
    const updateDimensions = () => {
      const result = calculateFlipbookDimensions();
      setIsMobile(result.isMobile);
      setDimensions({ width: result.width, height: result.height });
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  const goToPrevPage = useCallback(() => {
    flipBookRef.current?.pageFlip().flipPrev();
  }, []);

  const goToNextPage = useCallback(() => {
    flipBookRef.current?.pageFlip().flipNext();
  }, []);

  const onFlip = useCallback((e) => {
    setCurrentPage(e.data);
  }, []);

  const goToPage = useCallback((index) => {
    flipBookRef.current?.pageFlip().turnToPage(index);
  }, []);

  const openFullscreen = useCallback(() => {
    setIsFullscreen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeFullscreen = useCallback(() => {
    setIsFullscreen(false);
    document.body.style.overflow = '';
  }, []);

  const fullscreenPrev = useCallback(() => {
    if (currentPage > 0) {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      flipBookRef.current?.pageFlip().turnToPage(newPage);
    }
  }, [currentPage]);

  const fullscreenNext = useCallback(() => {
    if (currentPage < totalPages - 1) {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      flipBookRef.current?.pageFlip().turnToPage(newPage);
    }
  }, [currentPage, totalPages]);

  const currentPageTitle = currentPage < CATALOG_PAGES.length 
    ? CATALOG_PAGES[currentPage].title 
    : "Prendre rendez-vous";

  return (
    <div data-testid="catalogue-page" className="min-h-screen bg-[#1A1A1A]">
      {/* Fullscreen viewer */}
      {isFullscreen && (
        <FullscreenViewer
          pageIndex={currentPage}
          onClose={closeFullscreen}
          onPrev={fullscreenPrev}
          onNext={fullscreenNext}
          totalPages={totalPages}
        />
      )}

      {/* Hero */}
      <section className="pt-28 pb-4 bg-[#1A1A1A]">
        <div className="container-premium text-center">
          <p className="caption text-[#D4AF37] mb-2">Notre Catalogue</p>
          <h1 className="font-serif text-3xl md:text-4xl font-medium tracking-tight leading-[1.1] mb-2 text-white">
            Créez Vos Lunettes
          </h1>
          <p className="text-sm font-light text-white/60 max-w-lg mx-auto">
            Feuilletez notre catalogue pour découvrir toutes les options de personnalisation.
          </p>
        </div>
      </section>

      {/* Flipbook */}
      <section className="py-4 px-4">
        <div className="flex flex-col items-center">
          {/* Navigation hint & Fullscreen button */}
          <div className="flex items-center justify-center gap-4 mb-3">
            <p className="text-white/40 text-xs flex items-center gap-2">
              <ChevronLeft size={ICON_SIZE.SMALL} />
              Tournez les pages
              <ChevronRight size={ICON_SIZE.SMALL} />
            </p>
            <button
              onClick={openFullscreen}
              className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white/70 hover:text-white px-3 py-1.5 text-xs rounded transition-colors"
              aria-label="Plein écran"
            >
              <Maximize2 size={14} />
              Agrandir
            </button>
          </div>

          {/* Book container */}
          <div className="relative flex items-center justify-center">
            <NavArrow direction="left" disabled={currentPage === 0} onClick={goToPrevPage} />

            {/* Flipbook */}
            <div className="shadow-2xl rounded-lg overflow-hidden border border-white/10">
              <HTMLFlipBook
                ref={flipBookRef}
                width={dimensions.width}
                height={dimensions.height}
                size="fixed"
                minWidth={FLIPBOOK.MIN_WIDTH}
                maxWidth={FLIPBOOK.MAX_WIDTH}
                minHeight={FLIPBOOK.MIN_HEIGHT}
                maxHeight={FLIPBOOK.MAX_HEIGHT}
                showCover={true}
                mobileScrollSupport={true}
                onFlip={onFlip}
                className="flipbook"
                startPage={0}
                drawShadow={true}
                flippingTime={500}
                usePortrait={true}
                startZIndex={0}
                autoSize={false}
                maxShadowOpacity={0.3}
                showPageCorners={true}
                disableFlipByClick={false}
              >
                {/* Catalog pages */}
                {CATALOG_PAGES.map((page, index) => (
                  <div key={page.id} className="page">
                    <PageComponent 
                      pageData={page} 
                      pageNumber={index + 1} 
                      totalPages={totalPages}
                      isLastPage={false}
                      dimensions={dimensions}
                    />
                  </div>
                ))}
                
                {/* Last page with RDV button */}
                <div key="rdv-page" className="page">
                  <PageComponent 
                    pageData={null} 
                    pageNumber={totalPages} 
                    totalPages={totalPages}
                    isLastPage={true}
                    dimensions={dimensions}
                  />
                </div>
              </HTMLFlipBook>
            </div>

            <NavArrow direction="right" disabled={currentPage >= totalPages - 1} onClick={goToNextPage} />
          </div>

          {/* Page indicator */}
          <PageDots totalPages={totalPages} currentPage={currentPage} onPageSelect={goToPage} />

          {/* Page title */}
          <p className="mt-3 text-white/60 text-sm">{currentPageTitle}</p>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-10 bg-[#F9F9F7]">
        <div className="container-premium text-center">
          <h2 className="font-serif text-2xl md:text-3xl mb-3 text-[#1A1A1A]">
            Une question sur notre catalogue ?
          </h2>
          <p className="text-[#666660] font-light mb-5 text-sm">
            Notre équipe est disponible pour vous accompagner.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="https://wa.me/33781477380"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-5 py-2.5 text-xs uppercase tracking-[0.1em] font-medium hover:bg-[#1da954] transition-colors duration-300"
            >
              WhatsApp
            </a>
            <Link
              to="/contact"
              className="btn-primary text-xs py-2.5 px-5"
            >
              Prendre Rendez-vous
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
